// src/components/Admin/Sidebar/Sidebar.tsx

import React from "react";
import {
  Box,
  Drawer,
  List,
  ListItemIcon,
  ListItemText,
  Typography,
  ListItemButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  AttachMoney as AttachMoneyIcon,
  MailOutline as MailOutlineIcon,
  EventAvailable as EventIcon, // Icon for Events
  MenuBook as ResourcesIcon,    // Icon for Resources
  PermMedia as MediaIcon,       // Icon for Media Library
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";


interface SidebarProps {
  mobileOpen: boolean;
  handleDrawerToggle: () => void;
}

const drawerWidth = 240;

const Sidebar: React.FC<SidebarProps> = ({ mobileOpen, handleDrawerToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));

  // Updated menu items including Media Library
  const menuItems = [
    { text: "Dashboard", icon: <DashboardIcon />, path: "/" },
    { text: "Donors", icon: <AttachMoneyIcon />, path: "/donors" },
    { text: "Volunteers", icon: <PeopleIcon />, path: "/volunteers" },
    { text: "Contact Submissions", icon: <MailOutlineIcon />, path: "/contact-submissions" },
    // Admin-specific menu items
    { text: "Events", icon: <EventIcon />, path: "/admin/events" },
    { text: "Resources", icon: <ResourcesIcon />, path: "/admin/resources" },
    { text: "Media Library", icon: <MediaIcon />, path: "/admin/media" },
    // New Email menu item
    { text: "Email", icon: <MailOutlineIcon />, path: "/admin/email" },

  ];

  const drawerContent = (
    <Box>
      {/* Sidebar Header */}
      <Box
        sx={{
          p: 2,
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
        }}
      >
        <Typography variant="h6" fontWeight="bold" textAlign="center">
          Admin Panel
        </Typography>
      </Box>

      {/* Sidebar Menu */}
      <List>
        {menuItems.map((item) => (
          <ListItemButton
            key={item.text}
            selected={location.pathname === item.path}
            onClick={() => {
              navigate(item.path);
              if (!isSmUp) {
                handleDrawerToggle();
              }
            }}
            sx={{
              "&:hover": { backgroundColor: theme.palette.action.hover },
              "&.Mui-selected": { backgroundColor: theme.palette.action.selected },
            }}
          >
            <ListItemIcon sx={{ color: theme.palette.primary.main }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItemButton>
        ))}
      </List>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      aria-label="sidebar navigation"
    >
      {/* Temporary drawer for mobile */}
      {!isSmUp && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              height: "100vh",       // fixed height
              overflowY: "hidden",   // disable vertical scrolling
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Permanent drawer for larger screens */}
      {isSmUp && (
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              height: "100vh",       // fixed height
              overflowY: "hidden",   // disable vertical scrolling
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
    </Box>
  );
};

export default Sidebar;
